import MicroModal from 'micromodal';
import SimpleCache from './SimpleCache';
import { formatter } from '../../app.config';

interface Terminal {
    id: string;
    name: string;
    icon: string;
    infoBlock?: string;
    callback?: (tid: string | null) => Promise<any>;
}

interface PaymentWidgetParams {
    modalId: string;
    container: string;
    template: string;
    successTemplate?: string;
    errorTemplate?: string;
    infoBlockTemplate?: string;
    api: string;
    terminals: Terminal[];
    css?: string[];
    cache?: SimpleCache | null;
    onSuccess?: (data: object) => void;
    onFail?: (data: object) => void;
    onClose?: (data: { isCompleted: boolean }) => void;
    beforeRegisterIntent?: (data: object) => object;
    afterRegisterIntent?: (response: any) => any;
}

export default class PaymentWidgetCore {
    private modalId: string;
    private container: string;
    private template: string;
    private successTemplate: string;
    private errorTemplate: string;
    private infoBlockTemplate: string;
    private activeTerminal: Terminal;
    private root: HTMLElement;
    private content!: HTMLElement;
    private cache: SimpleCache | null = null;
    private onSuccess: ((data: object) => void);
    private onFail: ((data: object) => void);
    private onClose: ((data: { isCompleted: boolean }) => void);
    private beforeRegisterIntent: ((data: object) => object);
    private afterRegisterIntent: ((response: any) => any);
    private paymentCompleted: boolean = false;
    private api: string;
    private terminals: Terminal[];
    private css: string[] = [];
    private amount: number | null = null;
    private orderId: string | null = null;
    private tid: string | null = null;
    private description: object = {};

    constructor(params: PaymentWidgetParams) {
        const {
            modalId,
            container,
            template,
            successTemplate = '#altapay-success-template',
            errorTemplate = '#altapay-error-template',
            infoBlockTemplate = '#altapay-info-template',
            api,
            terminals,
            css = [],
            cache = new SimpleCache(),
            // tslint:disable-next-line:no-empty
            onSuccess = () => {},
            // tslint:disable-next-line:no-empty
            onFail = () => {},
            // tslint:disable-next-line:no-empty
            onClose = () => {},
            beforeRegisterIntent = (data) => data,
            afterRegisterIntent = (rs) => rs,
        } = params;

        this.modalId = modalId;
        this.container = container;
        this.template = template;
        this.successTemplate = successTemplate;
        this.errorTemplate = errorTemplate;
        this.infoBlockTemplate = infoBlockTemplate;
        this.api = api;
        this.terminals = terminals;
        this.css = css;
        this.cache = cache;

        this.onSuccess = onSuccess;
        this.onFail = onFail;
        this.onClose = onClose;
        this.beforeRegisterIntent = beforeRegisterIntent;
        this.afterRegisterIntent = afterRegisterIntent;

        if (!this.template) {
            throw new Error('Template is not set');
        }
        if (!this.api) {
            throw new Error('API is not set');
        }
        if (!Array.isArray(this.terminals) || !this.terminals.length) {
            throw new Error('Terminals are not set');
        }

        this.init();
    }

    /**
     * Start process of payment
     *
     * @param {number} amount in cents
     * @param {string} orderId
     * @param {string|null} tid
     * @param {object} description description to order
     * @param {string|null} terminal
     */
    public open(amount: number, orderId: string, tid: string | null = null, description: object = {}, terminal: string | null = null) {
        // clear content from main block and remove all event listeners
        this.content.innerHTML = '';

        // TODO: valid data
        this.amount = amount;
        this.orderId = orderId;
        this.tid = tid;
        this.description = description;

        this.renderAmount();

        // make as active a first tab
        if (terminal) {
            this.setActiveTab(this.root.querySelector(`#support-terminals [data-terminal=${terminal}]`) as HTMLElement);
        } else {
            this.setActiveTab();
        }
        MicroModal.show(this.modalId, {
            onShow: () => {
                (document.querySelector('body') as HTMLBodyElement).style.overflow = 'hidden';
            },
            onClose: () => {
                (document.querySelector('body') as HTMLBodyElement).style.overflow = 'auto';
                this.close();
            },
        });
    }

    /**
     * Finish a process of payment
     */
    public close() {
        // reset state
        this.amount = null;
        this.orderId = null;
        this.tid = null;
        this.description = {};
        this.content.innerHTML = '';
        this.showTabs();
        // clear cache
        if (this.cache) {
            this.cache.flush();
        }
        window.removeEventListener('message', this.onMessage, false);
    }

    /**
     * Check status of payment
     *
     * @returns {boolean}
     */
    public isCompleted() {
        return this.paymentCompleted;
    }

    public setActiveTerminal(terminalId) {
        if (!terminalId) {
            return;
        }

        const tab: HTMLElement | null = this.root.querySelector(`#support-terminals [data-terminal=${terminalId}]`);

        if (tab) {
            this.setActiveTab(tab);
        }
    }

    /**
     * Set cache for Altapay responses
     */
    public setCache(cache: SimpleCache | null = null) {
        if (cache instanceof SimpleCache || cache === null) {
            this.cache = cache;
        }
    }

    public init() {
        MicroModal.init();
        this.render();
        window.addEventListener('message', this.onMessage.bind(this), false);
    }

    /**
     * Render app in the ShadowDom of container
     */
    public render() {
        const container = document.querySelector(this.container);
        if (!container) {
            throw new Error('Container is not set.');
        }

        container.attachShadow({ mode: 'open' });

        // register styles in the shadow container
        this.css.forEach((item) => {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = item;

            (container.shadowRoot as ShadowRoot).appendChild(link);
        });

        const node = this.getNodeFromTemplate(this.template);
        (container.shadowRoot as ShadowRoot).appendChild(node);

        this.root = node;

        this.content = this.root.querySelector('#payment-data') as HTMLElement;
        if (!this.content) {
            throw new Error('Content not found');
        }

        this.renderTabs();
    }

    public renderAmount() {
        this.root.querySelectorAll('.amount').forEach((el) => (el as HTMLElement).innerText = formatter.asCurrency((this.amount as number) / 100, null, false));
    }

    /**
     * Render tabs by a terminal list
     * Add listener on tabs
     * Set a first tab like an active
     */
    public renderTabs() {
        const tabsEl = this.root.querySelector('#support-terminals') as HTMLElement;
        if (!tabsEl) {
            throw new Error('Payment types list is not set.');
        }

        this.terminals.forEach((terminal) => {
            const tab = document.createElement('button');
            tab.classList.add('tabs__item');
            tab.dataset.terminal = terminal.id;

            const icon = document.createElement('span');
            icon.classList.add('custom-icon');
            icon.classList.add(terminal.icon);
            // icon.classList.add('material-symbols-outlined');
            // icon.appendChild(document.createTextNode(terminal.icon));
            // icon.classList.add('custom-icon');
            // icon.classList.add(terminal.icon);

            tab.appendChild(icon);
            tab.appendChild(document.createTextNode(terminal.name));

            tabsEl.appendChild(tab);
        });

        // for smoother display of the block
        setTimeout(() => {
            tabsEl.style.overflowX = 'auto';
        }, 300);

        // add listener on change tabs
        tabsEl.addEventListener('click', this.onTabClick.bind(this));
    }

    public onTabClick(e) {
        let el;
        if (e && e.target && !e.target.classList.contains('tabs__item')) {
            el = e.target.closest('.tabs__item');
        } else {
            el = e.target;
        }

        this.setActiveTab(el);
    }

    public setActiveTab(el: HTMLElement | null = null) {
        const tabs = this.root.querySelectorAll('#support-terminals .tabs__item');
        tabs.forEach((tab) => {
            tab.classList.remove('active');
        });
        const element = (el || tabs[0]) as HTMLElement;
        element.classList.add('active');
        this.activeTerminal = this.terminals.find((item) => item.id === element.dataset.terminal) as Terminal;
        this.root.setAttribute('data-gateway', this.activeTerminal.id);
        element.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });

        this.renderContent();
    }

    public renderContent(ignoreConfirmation = false) {
        // clear block and remove all listeners
        this.content.innerHTML = '';

        if (!ignoreConfirmation && Object.prototype.hasOwnProperty.call(this.activeTerminal, 'infoBlock')) {
            this.showInfoBlock(this.content);
        } else {
            this.renderIFrame(this.content);
        }
    }

    public renderIFrame(el) {
        this.showLoader();
        this.fetchPaymentData()
            .then(({ url = null, redirectUrl = null }) => {
                // url = 'https://6f3b-2a02-a311-434a-d880-6aff-2f5d-56ca-d272.ngrok-free.app/altapay/fail/123/uk';
                if (redirectUrl !== null) {
                    window.location.href = redirectUrl;
                    return;
                }
                if (url !== null) {
                    this.createIFrame(el, url).finally(() => this.hideLoader());
                    return;
                }
                this.hideLoader();
            })
            .catch(() => {
                this.hideLoader();
            });
    }

    public createIFrame(el, url) {
        return new Promise((resolve) => {
            const iframe = this.root.querySelector('#altapay-iframe') as HTMLElement;

            if (iframe === null) {
                const frame = document.createElement('iframe');

                frame.className = 'PensioEmbeddedPaymentWindow';
                // frame.src = url;
                frame.src = `${url}&embeddedPaymentWindow=true&handleRedirectInsideIFrame=false`; // @ignore-line
                frame.setAttribute('id', 'altapay-iframe');
                frame.setAttribute('allowTransparency', 'true');
                frame.setAttribute('frameBorder', '0');

                // add to DOM
                el.appendChild(frame);

                frame.addEventListener('load', () => {
                    return resolve(frame);
                });
            } else {
                (iframe as any).src = url; // ?????
                return resolve(iframe);
            }
        });
    }

    public onPayButtonClick() {
        if (Object.prototype.hasOwnProperty.call(this.activeTerminal, 'callback')
            && typeof this.activeTerminal.callback === 'function'
        ) {
            this.activeTerminal.callback(this.tid)
                .then((res) => {
                    this.hideTabs();
                    this.showSuccessMessage(res);
                    this.paymentCompleted = true;
                    this.onSuccess({terminal: { ...this.activeTerminal }});
                })
                .catch(() => {
                    this.showErrorMessage();
                });
        } else {
            this.renderContent(true);
        }
    }

    public fetchPaymentData() {
        const data: object = this.beforeRegisterIntent({
            amount: this.amount,
            orderId: this.orderId,
            tid: this.tid,
            description: this.description,
            terminal: this.activeTerminal.id,
        });

        // const params = {
        //     embeddedPaymentWindow: true,
        //     handleRedirectInsideIFrame: false
        // };

        const cache = this.cache ? this.cache.getCache(data) : null;
        if (cache) {
            return Promise.resolve(cache);
        }

        return fetch(this.api, {
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        })
            .then((response) => {
                if (response.status >= 200 && response.status <= 299) {
                    return response.json();
                } else {
                    throw Error(response.statusText);
                }
            })
            .then((response) => this.afterRegisterIntent(response))
            .then((response) => {
                if (this.cache) {
                    this.cache.setCache(data, response);
                }

                return response;
            })
            .catch((error) => {
                this.showErrorMessage();
                return Promise.reject(error);
            });
    }

    /**
     * We block inactive tabs to avoid accidental calls
     */
    public blockInactiveTabs() {
        const tabs = this.root.querySelectorAll('#support-terminals .tabs__item');
        tabs.forEach((tab) => {
            if (!(tab instanceof HTMLElement)) {
                return;
            }

            const isActive = tab.classList.contains('active');
            if (!isActive) {
                if (tab.hasAttribute('disabled')) {
                    tab.removeAttribute('disabled');
                } else {
                    tab.setAttribute('disabled', 'disabled');
                }
            }
        });
    }

    public showLoader() {
        const loader = this.root.querySelector('.loader') as HTMLElement;
        loader.classList.add('show');

        this.blockInactiveTabs();
    }

    public hideLoader() {
        const loader = this.root.querySelector('.loader') as HTMLElement;
        loader.classList.remove('show');

        this.blockInactiveTabs();
    }

    public showInfoBlock(el) {
        const node = this.getNodeFromTemplate(this.infoBlockTemplate);
        const text = node.querySelector('.text') as HTMLElement;
        if (text) {
            text.innerHTML = this.activeTerminal.infoBlock || '';
        }

        el.appendChild(node);
        const button = el.querySelector('.actions__button') as HTMLElement;
        if (!button) {
            return;
        }

        button.addEventListener('click', this.onPayButtonClick.bind(this));
    }

    public showErrorMessage() {
        const message = this.getNodeFromTemplate(this.errorTemplate);
        const contentContainer = this.root.querySelector('#payment-data') as HTMLElement;

        contentContainer.innerHTML = '';
        contentContainer.appendChild(message);

        const button = contentContainer.querySelector('input[type=button]') as HTMLElement;
        if (button) {
            button.addEventListener('click', () => {
                this.renderContent();
            });
        }
    }

    /**
     * @var {string} message
     * @param message
     */
    public showSuccessMessage(message = '') {
        const template = this.getNodeFromTemplate(this.successTemplate);
        const contentContainer = this.root.querySelector('#payment-data') as HTMLElement;

        contentContainer.innerHTML = message;
        contentContainer.appendChild(template);

        const button = contentContainer.querySelector('input[type=button]') as HTMLElement;
        button.addEventListener('click', this.tryToClose.bind(this));
    }

    /**
     * Process a message:
     *  - payment_completed - when the payment ends successfully
     *  - payment_failed
     *  - close
     *
     * @param data
     */
    public onMessage({ data }) {
        let result = {};

        const isString = (obj) => {
            return obj !== undefined && obj !== null && obj.constructor === String;
        };

        const isResultMessage = (msg) => {
            return isString(msg) && ((msg).includes('payment_completed') || (msg).includes('payment_failed'));
        };

        const isCloseMessage = (msg) => {
            return isString(msg) && (msg === 'close');
        };

        if (isResultMessage(data)) {
            result = JSON.parse(data);
            this.paymentResult(result);
        } else if (isCloseMessage(data)) {
            this.tryToClose();
        }
    }

    public paymentResult(data) {
        const { id, ...rest } = data;
        this.hideTabs();

        if (id === 'payment_completed') {
            this.paymentCompleted = true;
            this.onSuccess(rest);
        } else if (id === 'payment_failed') {
            this.onFail(rest);
        }
    }

    public tryToClose() {
        if (!this.paymentCompleted) {
            this.showTabs();
            this.renderContent();
            return;
        }
        this.onClose({ isCompleted: this.paymentCompleted });
        MicroModal.close(this.modalId);
    }

    public hideTabs() {
        const header = this.root.querySelector('header.collapsible') as HTMLElement;
        if (!header.classList.contains('collapsed')) {
            header.classList.add('collapsed');
        }
    }

    public showTabs() {
        const header = this.root.querySelector('header.collapsible') as HTMLElement;
        header.classList.remove('collapsed');
    }

    /**
     * Get Node from template
     */
    public getNodeFromTemplate(selector) {
        const tpl = (document.querySelector(selector) || this.root.querySelector(selector)) as HTMLElement;
        if (!tpl) {
            throw new Error(`Template with selector '${selector}' is not found.`);
        }

        return (tpl as any).cloneNode(true);
    }
}
